"use client";

/**
 * A framed slideshow of makers' graphics for the /launch manifesto (Gordon
 * 2026-10-29: "add some slideshow style graphics taken from the users graphics
 * media vault on the manifesto section"). Same rhythm as the records — a 1 s
 * hold, then the next image fades in over 4 s — while the frame slowly
 * breathes in and out. Reduced-motion users get the fades without the drift.
 */

import { useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { FADE_MS, useImageCycle } from "@/components/launch/vinyl-disc";

export function GraphicSlideshow({ images, className }: { images: string[]; className?: string }) {
  const { index, fading } = useImageCycle(images.length);
  // The frame stays invisible until its first image has arrived — never an
  // empty dark box on stage.
  const [ready, setReady] = useState(false);

  if (images.length === 0) return null;
  const current = images[index % images.length];
  const next = images[(index + 1) % images.length];

  return (
    <div className={cn("relative aspect-square shrink-0 overflow-hidden rounded-3xl bg-[var(--launch-bg)] shadow-2xl ring-1 ring-black/10 ", className)} aria-hidden>
      {/* The whole frame breathes in and out, so a swap never jumps in scale. */}
      <div className="bb-slideshow-drift absolute inset-0">
        <Image src={current} alt="" fill sizes="(min-width: 1024px) 34vw, 90vw" loading="eager" className="object-cover" onLoad={() => setReady(true)} />
        <Image
          src={next}
          alt=""
          fill
          sizes="(min-width: 1024px) 34vw, 90vw"
          loading="eager"
          // Fades in over FADE_MS; the reset afterwards is instant (the layer
          // underneath already shows the same image), so nothing fades back.
          className={cn("object-cover ease-in-out", fading ? "opacity-100 transition-opacity" : "opacity-0 transition-none")}
          style={{ transitionDuration: fading ? `${FADE_MS}ms` : "0ms" }}
        />
      </div>
    </div>
  );
}
